import React, { useCallback, useState } from 'react';
import { useFocusEffect } from '@react-navigation/native';

import {
  AccountScreenContainer,
  AccountSweetpassMembershipLoading,
  SweetpassPaymentMethodAddModal,
  SweetpassPaymentMethodEditModal,
} from '@order/components';
import { useTelemetry } from '@order/Telemetry';

import type { SweetpassMembershipScreenProps } from '../../navigation/AppNavigation.props';
import { AccountSweetpassMembershipError } from './components';
import {
  useSweetpassAccountContentfulData,
  useSweetpassMembershipContent,
  useSweetpassMembershipData,
  useSweetpassMembershipFailureEvent,
  useSweetpassMembershipSuccessEvent,
} from './hooks';
import { SweetpassMembershipScreenContent } from './SweetpassMembershipScreenContent';

export const SweetpassMembershipScreen = (
  props: SweetpassMembershipScreenProps,
) => {
  // ─── Contentful Data ─────────────────────────────────────────────────

  const { content, isLoadingContentfulData } =
    useSweetpassAccountContentfulData();

  // ─── Membership Data ─────────────────────────────────────────────────

  const {
    tierStatus,
    tier,
    nextTier,
    freeTrialPlanId,
    planPrice,
    billingFrequencyUnit,
    hasPaidTierHistory,
    isEligibleToFreeTrial,
    joinDate,
    activeSubscription,
    paymentMethods,
    loyaltyProfileError,
    hasMembershipError,
    isSweetpassMembershipLoading,
    cancelSubscriptionAtCycleEnd,
    reactivateSubscription,
    refetchMembership,
  } = useSweetpassMembershipData();

  // ─── Refetch On Focus ────────────────────────────────────────────────

  useFocusEffect(refetchMembership);

  // ─── Telemetry ───────────────────────────────────────────────────────

  useSweetpassMembershipSuccessEvent({
    isLoading: isSweetpassMembershipLoading,
    hasLoyaltyProfile: Boolean(tier) && !hasMembershipError,
  });

  useSweetpassMembershipFailureEvent({
    isLoading: isSweetpassMembershipLoading,
    hasLoyaltyProfile: Boolean(tier) && !hasMembershipError,
    loyaltyProfileError,
  });

  if (isLoadingContentfulData || isSweetpassMembershipLoading) {
    return <AccountSweetpassMembershipLoading />;
  }

  if (hasMembershipError) {
    return <AccountSweetpassMembershipError />;
  }

  return (
    <SweetpassMembershipScreenContainer
      {...props}
      content={content}
      tierStatus={tierStatus}
      tier={tier}
      nextTier={nextTier}
      freeTrialPlanId={freeTrialPlanId}
      planPrice={planPrice}
      billingFrequencyUnit={billingFrequencyUnit}
      hasPaidTierHistory={hasPaidTierHistory}
      isEligibleToFreeTrial={isEligibleToFreeTrial}
      joinDate={joinDate}
      activeSubscription={activeSubscription}
      paymentMethods={paymentMethods}
      cancelSubscriptionAtCycleEnd={cancelSubscriptionAtCycleEnd}
      reactivateSubscription={reactivateSubscription}
      refetchMembership={refetchMembership}
    />
  );
};

export const SweetpassMembershipScreenContainer = (
  props: SweetpassMembershipScreenContainerProps,
) => {
  const { navigation } = props;
  const { track } = useTelemetry();

  // ─── State ───────────────────────────────────────────────────────────

  const [editingPaymentMethodId, setEditingPaymentMethodId] = useState('');
  const [isAddingPaymentMethod, setIsAddingPaymentMethod] = useState(false);

  // ─── Callbacks ───────────────────────────────────────────────────────

  const subscribeHandler = useCallback(
    async (planId?: string) => {
      navigation.navigate('Modal', {
        screen: 'SweetpassUpgrade',
        params: { planId },
      });
      track('sweetpass_account.upgrade_cta.tapped');
    },
    [navigation, track],
  );

  const viewBillingHistoryHandler = useCallback(() => {
    navigation.navigate('Modal', { screen: 'SweetpassBillingHistory' });
    track('sweetpass_account.billing_history.tapped');
  }, [navigation, track]);

  const addPaymentMethodHandler = useCallback(() => {
    track('sweetpass_account.add_payment_cta.tapped');
    setIsAddingPaymentMethod(true);
  }, [track]);

  const closeAddingPaymentMethodModal = useCallback(() => {
    setIsAddingPaymentMethod(false);
  }, []);

  const viewPaymentMethodHandler = useCallback(
    (paymentMethodId: string) => {
      track('sweetpass_account.add_payment_cta.tapped');
      setEditingPaymentMethodId(paymentMethodId);
    },
    [track],
  );

  const closeEditingPaymentMethodModal = useCallback(() => {
    setEditingPaymentMethodId('');
  }, []);

  // ─── Contentful ──────────────────────────────────────────────────────

  const sweetpassMembershipContent = useSweetpassMembershipContent({
    ...props,
    subscribeHandler,
    viewPaymentMethodHandler,
    addPaymentMethodHandler,
    viewBillingHistoryHandler,
  });

  return (
    <AccountScreenContainer screen="sweetpass-membership">
      <SweetpassMembershipScreenContent
        testID="sweetpass-membership" // this testID goes deep, will remove in a follow up PR.
        sweetpassMembershipContent={sweetpassMembershipContent}
      />

      <SweetpassPaymentMethodAddModal
        visible={isAddingPaymentMethod}
        onRequestClose={closeAddingPaymentMethodModal}
      />

      <SweetpassPaymentMethodEditModal
        paymentMethodId={editingPaymentMethodId}
        visible={Boolean(editingPaymentMethodId)}
        onRequestClose={closeEditingPaymentMethodModal}
      />
    </AccountScreenContainer>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type SweetpassMembershipScreenContainerProps = SweetpassMembershipScreenProps &
  Omit<
    ReturnType<typeof useSweetpassMembershipData>,
    | 'loyaltyProfileError'
    | 'hasMembershipError'
    | 'isSweetpassMembershipLoading'
  > &
  Omit<
    ReturnType<typeof useSweetpassAccountContentfulData>,
    'isLoadingContentfulData'
  >;
